import React from 'react'
import AuthProvider from './components/AuthProvider'
import Layout from './components/layout/Layout'
import { useAuth } from './hooks/useAuth'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import UserManagementPage from './pages/UserManagementPage'
import ReportManagementPage from './pages/ReportManagementPage'
import CouponManagementPage from './pages/CouponManagementPage'
import QuestionBankManagementPage from './pages/QuestionBankManagementPage'
import BoardManagementPage from './pages/BoardManagementPage'
import AchievementManagementPage from './pages/AchievementManagementPage'
import ExamMonitoringPage from './pages/ExamMonitoringPage'
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage'
import BoardPage from './pages/BoardPage'
import ExamPage from './pages/ExamPage'
import ExamResultsPage from './pages/ExamResultsPage'
import ExamHistoryPage from './pages/ExamHistoryPage'
import ExamSelectionPage from './pages/ExamSelectionPage'
import ProfilePage from './pages/ProfilePage'
import RankingPage from './pages/RankingPage'
import CohortPage from './pages/CohortPage'
import SubscriptionPage from './pages/SubscriptionPage'
import { UpgradeProvider } from './hooks/useUpgrade'
import { ErrorBoundary, LoadingState } from './components/ui'
import type { NotificationLink } from './components/layout/NotificationBell'


const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const [currentPage, setCurrentPage] = React.useState<string>('dashboard')
  const [examSessionId, setExamSessionId] = React.useState<string | null>(null)
  const [resultsSessionId, setResultsSessionId] = React.useState<string | null>(null)
  const [questionEditTarget, setQuestionEditTarget] = React.useState<{
    examType: string
    questionId?: string
  } | null>(null)
  const forceLoginPage = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('forceLogin') === '1'
  }, [])

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const sessionId = urlParams.get('sessionId')
    const hash = window.location.hash
    const pathname = window.location.pathname
    const code = urlParams.get('code')
    

    
    if ((hash === '#results' || pathname === '/results') && sessionId) {
      setResultsSessionId(sessionId)
      setCurrentPage('results')
    }
  }, [])

  if (isLoading) {
    return <LoadingState fullscreen label="로그인 정보를 확인하는 중..." />
  }


  
  if (!isAuthenticated || forceLoginPage) {
    return <LoginPage />
  }

  const handlePageChange = (page: string) => {
    setCurrentPage(page)
    setQuestionEditTarget(null)
    // 페이지 변경 시 세션 ID 초기화
    if (page !== 'exam') {
      setExamSessionId(null)
    }
    if (page !== 'results') {
      setResultsSessionId(null)
    }
  }

  const handleStartExam = (sessionId: string) => {
    setExamSessionId(sessionId)
    setCurrentPage('exam')
  }

  const handleGoToExamSelection = () => {
    setCurrentPage('exam')
  }

  const handleViewResults = (sessionId: string) => {
    setResultsSessionId(sessionId)
    setCurrentPage('results')
  }

  const handleEditReportedQuestion = (examType: string, questionId: string) => {
    setQuestionEditTarget({ examType, questionId })
    setCurrentPage('question-management')
  }

  // 메시지함 알림을 눌렀을 때 해당 화면으로 이동
  const handleNotificationNavigate = (link: NotificationLink) => {
    if (link.page === 'results' && 'sessionId' in link && link.sessionId) {
      handleViewResults(link.sessionId)
      return
    }
    if (link.page === 'question-management') {
      setExamSessionId(null)
      setResultsSessionId(null)
      setQuestionEditTarget('examType' in link && link.examType ? { examType: link.examType } : null)
      setCurrentPage('question-management')
      return
    }
    handlePageChange(link.page)
  }

  const handleExitExam = () => {
    setExamSessionId(null)
    setCurrentPage('dashboard')
  }

  const handleBackFromResults = () => {
    setResultsSessionId(null)
    setCurrentPage('dashboard')
  }

  const renderCurrentPage = () => {

    
    switch (currentPage) {
      case 'dashboard':
        return (
          <DashboardPage 
            onStartExam={handleStartExam}
            onViewResults={handleViewResults}
            onGoToExamSelection={handleGoToExamSelection}
          />
        )
      case 'exam':
        return examSessionId ? (
          <ExamPage 
            sessionId={examSessionId}
            onExit={handleExitExam}
          />
        ) : (
          <ExamSelectionPage
            onStartExam={handleStartExam}
            onExit={handleExitExam}
          />
        )
      case 'results':
        return resultsSessionId ? (
          <ExamResultsPage 
            sessionId={resultsSessionId}
            onBack={handleBackFromResults}
            userId={new URLSearchParams(window.location.search).get('userId') || undefined}
            onStartExam={new URLSearchParams(window.location.search).get('userId') ? undefined : handleStartExam}
          />
        ) : (
          <ExamHistoryPage
            onViewResults={handleViewResults}
            onStartExam={handleGoToExamSelection}
          />
        )
      case 'board':
        return <BoardPage />
      case 'ranking':
        return <RankingPage />
      case 'cohorts':
        return <CohortPage />
      case 'subscription':
        return <SubscriptionPage />
      case 'profile':
        return <ProfilePage onBack={() => setCurrentPage('dashboard')} onViewResults={handleViewResults} />

      case 'user-management':
        return <UserManagementPage />
      case 'question-bank-management':
        return <QuestionBankManagementPage />
      case 'question-management':
        return (
          <QuestionBankManagementPage
            initialTab="questions"
            initialExamType={questionEditTarget?.examType}
            initialQuestionId={questionEditTarget?.questionId}
          />
        )
      case 'report-management':
        return <ReportManagementPage onEditQuestion={handleEditReportedQuestion} />
      case 'coupon-management':
        return <CouponManagementPage />
      case 'coupon-logs':
        return <CouponManagementPage initialTab="logs" />
      case 'exam-type-management':
        return <QuestionBankManagementPage initialTab="exam-types" />
      case 'board-management':
        return <BoardManagementPage />
      case 'achievement-management':
        return <AchievementManagementPage />
      case 'exam-monitoring':
        return <ExamMonitoringPage />
      case 'analytics-dashboard':
        return <AnalyticsDashboardPage />

      default:
        return (
          <DashboardPage 
            onStartExam={handleStartExam}
            onViewResults={handleViewResults}
          />
        )
    }
  }

  return (
    <UpgradeProvider onOpenPlans={() => handlePageChange('subscription')}>
    <Layout
      currentPage={currentPage}
      onPageChange={handlePageChange}
      isFocusMode={Boolean(examSessionId)}
      onNavigate={handleNotificationNavigate}
    >
      <ErrorBoundary resetKey={currentPage}>{renderCurrentPage()}</ErrorBoundary>
    </Layout>
    </UpgradeProvider>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
